import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  CircularProgress,
  IconButton,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Divider,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  VideoCall as VideoCallIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { ZoomMtg } from '@zoom/meetingsdk';
import api, { handleCatch } from '../api';
import colors from '../common/Colors';
import { formatInBusinessTimezone, getBusinessNow, toBusinessTimezone } from '../utils/dateTimeHelper';
import { parseISO } from 'date-fns';
import MobileHeader from '../components/MobileHeader';

let zoomSdkPrepared = false;

const getClassJoinKey = (item) => item?.id || item?.onlineClassId || item?.meetingId;

const prepareZoomSdk = () => {
  if (zoomSdkPrepared) return;

  ZoomMtg.preLoadWasm();
  ZoomMtg.prepareWebSDK();
  zoomSdkPrepared = true;
};

const getZoomErrorMessage = (error) => {
  if (!error) return 'Failed to join class. Please try again.';
  if (typeof error === 'string') return error;
  if (error.errorCode === 4011) {
    return 'This Zoom meeting is hosted by another Zoom account. Use the same Zoom account as the Meeting SDK app for testing, or publish the Zoom app on Marketplace.';
  }
  return error.reason || error.message || error.errorMessage || 'Failed to join class. Please try again.';
};

const joinZoomClient = (meetingConfig) =>
  new Promise((resolve, reject) => {
    const { signature, meetingNumber, password, userName } = meetingConfig || {};

    if (!signature || !meetingNumber || !userName) {
      reject(new Error('Zoom join details are incomplete.'));
      return;
    }

    prepareZoomSdk();

    ZoomMtg.init({
      leaveUrl: `${window.location.origin}/online-classes`,
      patchJsMedia: true,
      disableInvite: true,
      disableCallOut: true,
      disableRecord: true,
      success: () => {
        ZoomMtg.join({
          signature,
          meetingNumber,
          userName,
          passWord: password || '',
          success: resolve,
          error: reject,
        });
      },
      error: reject,
    });
  });

const OnlineClassScreen = () => {
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [joiningClass, setJoiningClass] = useState(null);
  const [joinDialogOpen, setJoinDialogOpen] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('userToken');
      const response = await api.get(
        'api/school-common/online-class/get-online-class-info-student',
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      setItems(response.data || []);
    } catch (error) {
      console.error('Error loading online classes:', error);
      handleCatch(error);
      setItems([]);
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  };

  const onRefresh = async () => {
    await loadData();
  };

  const getClassStatus = (startDateTime, endDateTime) => {
    const now = getBusinessNow();
    const start = toBusinessTimezone(parseISO(startDateTime));
    const end = toBusinessTimezone(parseISO(endDateTime));
    if (now < start) return { status: 'upcoming', color: '#FF9800', text: 'Upcoming' };
    if (now >= start && now <= end) return { status: 'live', color: '#4CAF50', text: 'Live Now' };
    return { status: 'ended', color: '#999', text: 'Ended' };
  };

  const handleJoinClick = (item) => {
    setSelectedClass(item);
    setJoinDialogOpen(true);
  };

  const closeJoinDialog = () => {
    setJoinDialogOpen(false);
    setSelectedClass(null);
  };

  const markAttendance = async () => {
    await api.post('api/school/school-transaction/post-student-attendance-by-school-app', null);
  };

  const startMeeting = async () => {
    if (!selectedClass) return;
    const classJoinKey = getClassJoinKey(selectedClass);

    try {
      setJoiningClass(classJoinKey);
      setJoinDialogOpen(false);
      setNotification({
        open: true,
        message: `Preparing ${selectedClass.meetingName}...`,
        severity: 'info',
      });

      const signatureResponse = await api.post('api/school-common/online-class/zoom/signature', {
        onlineClassId: selectedClass.id || selectedClass.onlineClassId || null,
        meetingNumber: selectedClass.meetingId,
      });

      await joinZoomClient(signatureResponse.data);
      await markAttendance();

      setNotification({
        open: true,
        message: `Joined ${selectedClass.meetingName}. Attendance marked!`,
        severity: 'success',
      });
    } catch (error) {
      console.error('Online class error:', error);
      const message = error?.response ? handleCatch(error) : getZoomErrorMessage(error);
      setNotification({ open: true, message, severity: 'error' });
    } finally {
      setJoiningClass(null);
      setSelectedClass(null);
    }
  };

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#F5F7FA' }}>
      <MobileHeader
        title="Online Classes"
        showBack={true}
        rightAction={
          <IconButton onClick={onRefresh} disabled={loading} sx={{ color: '#fff' }}>
            <RefreshIcon />
          </IconButton>
        }
      />

      <Box sx={{ flex: 1, overflowY: 'auto', px: 1, pt: 1, pb: 2 }}>
        {initialLoading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 10 }}>
            <CircularProgress />
            <Typography sx={{ mt: 2, color: 'text.secondary', fontSize: 14 }}>Loading classes...</Typography>
          </Box>
        ) : items.length === 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 10, px: 3 }}>
            <Typography sx={{ fontSize: 64, mb: 1.5 }}></Typography>
            <Typography sx={{ fontWeight: 'bold', fontSize: 17, mb: 1, textAlign: 'center' }}>
              No Online Classes
            </Typography>
            <Typography sx={{ fontSize: 13, color: 'text.secondary', textAlign: 'center', mb: 3 }}>
              No scheduled online classes found. Check back later.
            </Typography>
            <Button variant="contained" startIcon={<RefreshIcon />} onClick={onRefresh}>
              Refresh
            </Button>
          </Box>
        ) : (
          items.map((item, index) => {
            const classStatus = getClassStatus(item.startDateTime, item.endDateTime);
            const isJoining = joiningClass === getClassJoinKey(item);
            const canJoin = classStatus.status === 'live' || classStatus.status === 'upcoming';
            return (
              <Box
                key={getClassJoinKey(item) || index}
                sx={{
                  bgcolor: '#fff',
                  borderRadius: 2,
                  boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                  mx: 0.5,
                  mt: 1,
                  overflow: 'hidden',
                }}
              >
                <Box sx={{ px: 2, pt: 2, pb: 1 }}>
                  {/* Title + status */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box sx={{ flex: 1, mr: 1 }}>
                      <Typography sx={{ fontWeight: 'bold', fontSize: 15, color: '#222', lineHeight: 1.3 }}>
                        {item.meetingName}
                      </Typography>
                      <Typography sx={{ fontSize: 13, color: colors.BLUE, fontWeight: '500', mt: 0.3 }}>
                         {item.subjectName}
                      </Typography>
                    </Box>
                    <Chip
                      label={classStatus.text}
                      size="small"
                      sx={{ bgcolor: classStatus.color, color: '#fff', fontWeight: 'bold', fontSize: 11 }}
                    />
                  </Box>
                  {/* Teacher */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <PersonIcon sx={{ fontSize: 14, color: '#888', mr: 0.5 }} />
                    <Typography sx={{ fontSize: 13, color: '#555' }}>{item.userName}</Typography>
                  </Box>
                  {/* Time */}
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <ScheduleIcon sx={{ fontSize: 14, color: '#888', mr: 0.5 }} />
                    <Typography sx={{ fontSize: 12, color: '#666' }}>
                      {formatInBusinessTimezone(item.startDateTime, 'dd MMM, hh:mm a')} {' '}
                      {formatInBusinessTimezone(item.endDateTime, 'hh:mm a')}
                    </Typography>
                  </Box>
                </Box>

                <Divider />

                <Box sx={{ px: 2, py: 1.5 }}>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={
                      isJoining ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <VideoCallIcon />
                      )
                    }
                    onClick={() => handleJoinClick(item)}
                    disabled={!canJoin || isJoining}
                    sx={{
                      bgcolor: isJoining ? '#FF9800' : canJoin ? (classStatus.status === 'live' ? '#4CAF50' : colors.BLUE) : '#ccc',
                      height: 44,
                      borderRadius: 22,
                      fontWeight: 'bold',
                      textTransform: 'none',
                      fontSize: 14,
                      '&:hover': {
                        bgcolor: canJoin ? (classStatus.status === 'live' ? '#388E3C' : '#0052A3') : '#ccc',
                      },
                    }}
                  >
                    {isJoining ? 'Joining...' : canJoin ? 'Join Class' : 'Class Ended'}
                  </Button>
                </Box>
              </Box>
            );
          })
        )}
      </Box>

      {/* Join Dialog */}
      {selectedClass && (
        <Dialog open={joinDialogOpen} onClose={closeJoinDialog}>
          <DialogTitle>Join Online Class</DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Joining <strong>{selectedClass.meetingName}</strong> inside the app.
            </Typography>
            <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Meeting ID:</strong> {selectedClass.meetingId}
              </Typography>
              <Typography variant="body2">
                <strong>Password:</strong> {selectedClass.meetingPassword}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Your attendance will be marked after Zoom confirms you joined.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeJoinDialog}>Cancel</Button>
            <Button onClick={startMeeting} variant="contained" startIcon={<VideoCallIcon />}>
              Join in App
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OnlineClassScreen;
